import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { EM_DASH } from "@/lib/utils";

import {
  fetchQualityRmas,
  resolveQualityRmaArrivalScan,
  type QualityRma,
} from "./rmaApi";

const HELP_TEXT = [
  "Receiving hands the carton here after the count checks out.",
  "Scan the carton tag or the RMA number, or choose a received RMA, then log each device.",
].join(" ");

const QC_ANALYSIS_QUEUE_COLUMNS: HubDataTableColumn<QualityRma>[] = [
  {
    id: "rmaNumber",
    header: "RMA",
    sortKey: "rmaNumber",
    overflow: "nowrap",
    sortValue: (row) => row.rmaNumber,
    cell: (row) => <span className="font-medium text-sky-800">{row.rmaNumber}</span>,
  },
  {
    id: "endUser",
    header: "End user",
    sortKey: "endUser",
    sortValue: (row) => row.endUserName ?? "",
    cell: (row) => row.endUserName ?? EM_DASH,
  },
  {
    id: "complaint",
    header: "Complaint",
    sortKey: "complaint",
    sortValue: (row) => row.complaint ?? "",
    cell: (row) => row.complaint ?? EM_DASH,
  },
  {
    id: "intakeCount",
    header: "Devices",
    sortKey: "intakeCount",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.intakeCount,
    cell: (row) => row.intakeCount,
  },
];

function arrivalScanHex(raw: string): string | null {
  const hex = raw.trim().toLowerCase().replace(/-/g, "");
  return /^[0-9a-f]{32}$/.test(hex) ? hex : null;
}

export function QualityQcAnalysisPage(): JSX.Element {
  const navigate = useNavigate();
  const [scan, setScan] = useState("");
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookingUp, setLookingUp] = useState(false);

  const listQuery = useQuery({
    queryKey: ["quality", "qc-analysis", "received"],
    queryFn: () => fetchQualityRmas({ status: "received", limit: 100 }),
  });

  const openRma = (rmaId: number): void => {
    navigate(`/quality/qc-analysis/${rmaId}`);
  };

  const handleScan = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const value = scan.trim();
    if (value === "") return;
    setLookupError(null);
    setLookingUp(true);
    try {
      if (arrivalScanHex(value) != null) {
        const resolved = await resolveQualityRmaArrivalScan(value);
        if (resolved.rma == null) {
          setLookupError("No RMA is linked to this tag.");
          return;
        }
        setScan("");
        openRma(resolved.rma.id);
        return;
      }
      const found = await fetchQualityRmas({ search: value, limit: 20 });
      const exact = found.rows.filter((row) => row.rmaNumber.toLowerCase() === value.toLowerCase());
      const match = exact[0] ?? (found.rows.length === 1 ? found.rows[0] : null);
      if (match == null) {
        setLookupError("No RMA matches that scan.");
        return;
      }
      setScan("");
      openRma(match.id);
    } catch (error) {
      setLookupError(error instanceof ApiError ? error.message : "Could not look up that scan.");
    } finally {
      setLookingUp(false);
    }
  };

  const rows = useMemo(() => listQuery.data?.rows ?? [], [listQuery.data?.rows]);

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint={HELP_TEXT}>QC Analysis</PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Scan the carton</CardTitle>
        </CardHeader>
        <CardBody>
          <form className="flex flex-wrap items-end gap-2" onSubmit={(event) => void handleScan(event)}>
            <div className="min-w-[16rem] flex-1">
              <Label htmlFor="qc-analysis-scan">Carton tag or RMA number</Label>
              <Input
                id="qc-analysis-scan"
                value={scan}
                autoFocus
                placeholder="Scan the Receiving sticker or type the RMA number"
                onChange={(event) => setScan(event.target.value)}
              />
            </div>
            <Button type="submit" isLoading={lookingUp}>
              Open
            </Button>
          </form>
          {lookupError ? <p className="mt-2 text-sm text-rose-700">{lookupError}</p> : null}
        </CardBody>
      </Card>

      <HubDataTable
        title={
          listQuery.isLoading
            ? "Loading received RMAs…"
            : `${rows.length} received RMA${rows.length === 1 ? "" : "s"}`
        }
        columns={QC_ANALYSIS_QUEUE_COLUMNS}
        rows={rows}
        rowKey={(row) => row.id}
        loading={listQuery.isLoading}
        loadingMessage="Loading received RMAs…"
        error={listQuery.isError ? "Could not load received RMAs." : null}
        empty="No received RMAs are waiting. Scan a carton tag if Receiving just handed one over."
        onRowClick={(row) => openRma(row.id)}
        rowClassName={() => "cursor-pointer"}
      />
    </div>
  );
}
